import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

export function ApprovalFields({ value, onChange, ownerCodes = [], admin = false }) {
  const v = value;
  const codes = ownerCodes.filter((c) => c.pot === v.pot);
  const tp = v.third_party || [];
  const setTp = (i, k, x) => onChange({ ...v, third_party: tp.map((t, j) => (j === i ? { ...t, [k]: x } : t)) });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Pot</Label>
          <select className="h-10 w-full rounded-md border bg-background px-2 text-sm" value={v.pot} onChange={(e) => onChange({ ...v, pot: e.target.value, owner_code: "" })} data-testid="approve-pot-select">
            <option value="AM">AM</option>{admin && <option value="AMSM">AMSM (historic only)</option>}</select></div>
        <div className="space-y-1"><Label className="text-xs">Owner code</Label>
          <select className="h-10 w-full rounded-md border bg-background px-2 text-sm" value={v.owner_code} onChange={(e) => onChange({ ...v, owner_code: e.target.value })} data-testid="approve-owner-code-select">
            <option value="">Choose…</option>{codes.map((c) => <option key={c.code} value={c.code}>{c.code} – {c.meaning}</option>)}</select></div>
      </div>
      {v.pot === "AMSM" && <p className="text-xs font-semibold text-primary">AMSM is historic only – new projects normally go in AM.</p>}
      <div className="space-y-2"><Label className="text-xs">Third-party shares (Family Trust, Afzal's sister, co-investor…)</Label>
        {tp.map((t, i) => <div key={i} className="flex gap-2" data-testid={`third-party-row-${i}`}>
          <Input placeholder="Name" value={t.name} onChange={(e) => setTp(i, "name", e.target.value)} data-testid={`third-party-name-${i}`} />
          <Input placeholder="%" type="number" className="w-24" value={t.pct} onChange={(e) => setTp(i, "pct", e.target.value)} data-testid={`third-party-pct-${i}`} />
          <button onClick={() => onChange({ ...v, third_party: tp.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4 text-muted-foreground" /></button></div>)}
        <button className="inline-flex items-center gap-1 text-sm font-semibold text-primary" onClick={() => onChange({ ...v, third_party: [...tp, { name: "", pct: "" }] })} data-testid="third-party-add-btn"><Plus className="h-4 w-4" />Add share</button>
      </div>
    </div>
  );
}

export function DecisionDialog({ item, approve, ownerCodes, onClose, onDone }) {
  const [v, setV] = useState({ pot: "AM", owner_code: "", third_party: [] });
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const project = item?.kind === "project";
  const go = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/approvals/${item.id}/${approve ? "approve" : "reject"}`, { reason, extra: project && approve ? v : {} }); toast.success(approve ? "Approved" : "Rejected"); onDone(); onClose(); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{approve ? "Approve" : "Reject"} – {item?.title}</DialogTitle></DialogHeader>
        {item?.summary && <ul className="space-y-1 text-sm text-muted-foreground">{item.summary.map((s, i) => <li key={i}>{s}</li>)}</ul>}
        {project && approve && <ApprovalFields value={v} onChange={setV} ownerCodes={ownerCodes} />}
        <Textarea placeholder={approve ? "Note (optional)" : "Reason (required)"} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="decision-reason-input" />
        {err && <div className="text-sm font-medium text-primary" data-testid="decision-error">{err}</div>}
        <DialogFooter><Button disabled={busy} variant={approve ? "default" : "destructive"} onClick={go} data-testid="decision-confirm-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{approve ? "Approve" : "Reject"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
